import {
    GET_MISSION_OVERTIME_STATUS,
    GET_MISSION_VACATION_STATUS,
    GET_PAYROLL_BREAKDOWN,
} from "../../graphql/timesheet";
import {
    buildAbsence,
    buildMission,
    buildMissionInfo,
    buildMissionOvertimeStatus,
    buildMissionVacationStatus,
    buildPayrollBreakdown,
    buildPayrollBreakdownSummary,
    buildTimesheet,
    buildUser,
    buildUserInfo,
    buildWorkerInfo,
} from "../factories";
import { MockedAuthManager } from "../../contexts/AuthManager";
import { MockedProvider } from "@apollo/client/testing";
import React, { useMemo } from "react";
import {
    AbsenceType,
    EarningType,
    PayrollBreakdownStatus,
    PayrollSourceType,
    ProfileType,
    TimeSheet,
    TimeSheetState,
} from "../../__generated__/graphql";
import type { Meta, StoryObj } from "@storybook/react";
import PayrollBreakdownDetailsScreen from "../../screens/user/PayrollBreakdownDetailsScreen";
import { navigatorLogger } from "../navigator-mock";
import { GET_MISSION } from "../../graphql/core";
import { MockedProfileSwitcherManager } from "../../contexts/ProfileSwitcherManager";

/**
 * The way the MockProvider works is that we cannot modify the result() nor newData() functions
 * after its creation so we need to create a function that pass dynamic parameters using
 * IDs for the payroll breakdowns and mission.
 * The ID is created using the buildPayrollBreakdownInput
 */
const parsePayrollBreakdownId = (payrollBreakdownId: string) => {
    const parts = payrollBreakdownId.split("-");
    return {
        earningType: parts[0] as EarningType,
        timesheetsStatus: parts[1],
        pbStatus: parts[2] as PayrollBreakdownStatus,
        payrollSource: parts[3] as PayrollSourceType,
        by: parts[4],
    };
};

const buildPayrollBreakdownId = ({
    earningType,
    timesheetsStatus,
    payrollSource,
    pbStatus,
    by = "user",
}: {
    earningType: string;
    timesheetsStatus: string;
    payrollSource: PayrollSourceType;
    pbStatus: PayrollBreakdownStatus;
    by: "system" | "user";
}) => {
    return `${earningType}-${timesheetsStatus}-${pbStatus}-${payrollSource}-${by}`;
};

const MOCKS = [
    {
        request: {
            query: GET_PAYROLL_BREAKDOWN,
        },
        variableMatcher: () => true,
        newData: ({ payrollBreakdownId }: { payrollBreakdownId: string }) => {
            const { timesheetsStatus, earningType, pbStatus, payrollSource, by } =
                parsePayrollBreakdownId(payrollBreakdownId);
            let timeSheets: TimeSheet[] = [];
            if (timesheetsStatus === "completed") {
                timeSheets = [
                    buildTimesheet({
                        status: TimeSheetState.Submitted,
                    }),
                    buildTimesheet({
                        status: TimeSheetState.Submitted,
                    }),
                    buildTimesheet({
                        status: TimeSheetState.Submitted,
                    }),
                ];
            } else if (timesheetsStatus === "open") {
                timeSheets = [
                    buildTimesheet({
                        status: TimeSheetState.Open,
                    }),
                    buildTimesheet({
                        status: TimeSheetState.Submitted,
                    }),
                    buildTimesheet({
                        status: TimeSheetState.Accepted,
                    }),
                ];
            } else if (timesheetsStatus === "rejected") {
                timeSheets = [
                    buildTimesheet({
                        status: TimeSheetState.Open,
                    }),
                    buildTimesheet({
                        status: TimeSheetState.Rejected,
                    }),
                    buildTimesheet({
                        status: TimeSheetState.Accepted,
                    }),
                ];
            }
            return {
                data: {
                    getPayrollBreakdown: buildPayrollBreakdown(new Date(2024, 1, 1), {
                        status: pbStatus,
                        missionInfo: buildMissionInfo({ earningType, payrollSource }),
                        timesheets: timeSheets,
                        totalWork100: timesheetsStatus === "completed" ? 42.0 * 60 : 0,
                        totalWork125: timesheetsStatus === "completed" ? 2.0 * 60 : 0,
                        totalVacationDays: timesheetsStatus === "completed" ? "2.5" : "0",
                        totalWorkedDays: timesheetsStatus === "completed" ? "5" : "0",
                        submittedBy:
                            by === "system"
                                ? null
                                : buildUserInfo({
                                      firstName: "Jim",
                                      lastName: "Jones",
                                  }),
                        absences: [
                            buildAbsence(),
                            buildAbsence({
                                type: AbsenceType.Military,
                                start: "2024-02-01",
                                end: "2024-02-05",
                            }),
                        ],
                        summary: buildPayrollBreakdownSummary({
                            rows: [
                                {
                                    category: "salary",
                                    productKey: "salary_monthly_partial",
                                    quantity: 1,
                                    unitPrice: 432100,
                                },
                                {
                                    category: "salary",
                                    productKey: "salary_overtime_25",
                                    quantity: 20,
                                    unitPrice: 500,
                                },
                                {
                                    category: "salary",
                                    productKey: "salary_overtime_50",
                                    quantity: 10,
                                    unitPrice: 1000,
                                },
                            ],
                        }),
                    }),
                },
            };
        },
    },
    {
        variableMatcher: () => true,
        request: {
            query: GET_MISSION,
        },
        newData: () => ({
            data: {
                getMission: buildMission(),
            },
        }),
    },
    {
        variableMatcher: () => true,
        maxUsageCount: Number.POSITIVE_INFINITY,
        request: {
            query: GET_MISSION_VACATION_STATUS,
        },
        result: () => {
            return {
                data: {
                    getMissionVacationStatus: buildMissionVacationStatus(),
                },
            };
        },
    },
    {
        variableMatcher: () => true,
        maxUsageCount: Number.POSITIVE_INFINITY,
        request: {
            query: GET_MISSION_OVERTIME_STATUS,
        },
        result: () => {
            return {
                data: {
                    getMissionOvertimeStatus: buildMissionOvertimeStatus(),
                },
            };
        },
    },
];

const StoryComponent = ({
    status,
    profileType,
    earningType,
    payrollSource,
    timesheetsStatus,
    by,
}: {
    status: PayrollBreakdownStatus;
    profileType: ProfileType;
    earningType: EarningType;
    payrollSource: PayrollSourceType;
    timesheetsStatus: string;
    by: "system" | "user";
}) => {
    const user = useMemo(
        () =>
            buildUser({
                workerInfo: buildWorkerInfo(),
            }),
        [],
    );

    return (
        <MockedAuthManager user={user}>
            <MockedProfileSwitcherManager profileType={profileType}>
                <MockedProvider mocks={MOCKS}>
                    <PayrollBreakdownDetailsScreen
                        /* @ts-expect-error Stack Component need a complex navigation handler */
                        route={{
                            params: {
                                payrollBreakdownId: buildPayrollBreakdownId({
                                    earningType,
                                    timesheetsStatus,
                                    payrollSource,
                                    pbStatus: status,
                                    by,
                                }),
                            },
                        }}
                        /* @ts-expect-error Stack Component need a complex navigation handler */
                        navigation={navigatorLogger}
                    />
                </MockedProvider>
            </MockedProfileSwitcherManager>
        </MockedAuthManager>
    );
};

const meta = {
    title: "Screens/PayrollBreakdown Details",
    component: StoryComponent,
    argTypes: {
        status: {
            options: Object.values(PayrollBreakdownStatus),
            control: {
                type: "select",
            },
        },
        profileType: {
            options: Object.values(ProfileType),
            control: {
                type: "select",
            },
        },
        earningType: {
            options: Object.values(EarningType),
            control: {
                type: "select",
            },
        },
        timesheetsStatus: {
            options: ["completed", "open", "rejected"],
            control: {
                type: "select",
            },
        },
        payrollSource: {
            options: Object.values(PayrollSourceType),
            control: {
                type: "select",
            },
        },
    },
} satisfies Meta<typeof StoryComponent>;

export default meta;

type Story = StoryObj<typeof meta>;

export const ClientHourly: Story = {
    args: {
        status: PayrollBreakdownStatus.Submitted,
        profileType: ProfileType.Client,
        earningType: EarningType.Hourly,
        payrollSource: PayrollSourceType.Lift,
        timesheetsStatus: "completed",
        by: "user",
    },
};

export const ClientMonthly: Story = {
    args: {
        status: PayrollBreakdownStatus.Submitted,
        profileType: ProfileType.Client,
        earningType: EarningType.Monthly,
        payrollSource: PayrollSourceType.Lift,
        timesheetsStatus: "completed",
        by: "user",
    },
};

export const WorkerHourlyOpen: Story = {
    args: {
        status: PayrollBreakdownStatus.Open,
        profileType: ProfileType.Worker,
        earningType: EarningType.Hourly,
        payrollSource: PayrollSourceType.Lift,
        timesheetsStatus: "open",
        by: "user",
    },
};

export const WorkerMonthlyOpenManual: Story = {
    args: {
        status: PayrollBreakdownStatus.Open,
        profileType: ProfileType.Worker,
        earningType: EarningType.Monthly,
        payrollSource: PayrollSourceType.ManualClient,
        timesheetsStatus: "completed",
        by: "user",
    },
};

export const WorkerMonthlySubmittedManual: Story = {
    args: {
        status: PayrollBreakdownStatus.Submitted,
        profileType: ProfileType.Worker,
        earningType: EarningType.Monthly,
        payrollSource: PayrollSourceType.ManualClient,
        timesheetsStatus: "completed",
        by: "user",
    },
};

export const WorkerMonthlyAutoSubmittedManual: Story = {
    args: {
        status: PayrollBreakdownStatus.Submitted,
        profileType: ProfileType.Worker,
        earningType: EarningType.Monthly,
        payrollSource: PayrollSourceType.ManualClient,
        timesheetsStatus: "completed",
        by: "system",
    },
};
